import React, { useState } from 'react';
import useSWR from 'swr';
import { FullPageLoader } from 'components/FullPageLoader';
import { permissionsService } from 'views/services/permissionsService';
import { RolePermissionsCard } from './components/RolePermissionsCard';
import { CreateRoleModal } from './components/CreateRoleModal';
import { Button } from 'components/button';
import { toast } from 'App';
import {
  ReadPermissionGate,
  CreatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { EAdminRole } from 'views/types/admins';

export const PermissionManagement = () => {
  const [updating, setUpdating] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const {
    data: rolesData,
    error,
    isLoading,
    mutate,
  } = useSWR(
    '/api/admin/permissions/roles',
    () => permissionsService.getAllRoles(),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 30000,
    }
  );

  const roles = React.useMemo(() => {
    if (!rolesData) return [];

    return rolesData.filter(role => role.name !== EAdminRole.SUPER_ADMIN);
  }, [rolesData]);

  const handleUpdatePermissions = async (
    roleId: string,
    resource: string,
    newActions: string[]
  ) => {
    try {
      setUpdating(roleId);

      const currentRole = roles.find(r => r.id === roleId);

      if (!currentRole) {
        console.error('Role not found:', roleId);
        toast({
          title: 'Error',
          description: 'Role not found',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const apiPermissions = {
        [resource]: {
          read: newActions.includes('read'),
          create: newActions.includes('create'),
          update: newActions.includes('update'),
          delete: newActions.includes('delete'),
        },
      };

      await permissionsService.updateRolePermissions(roleId, apiPermissions);

      await mutate();

      toast({
        title: 'Success',
        description: 'Permissions updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: `Failed to update permissions: ${err instanceof Error ? err.message : 'Unknown error'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteSuccess = async () => {
    await mutate();
  };

  const handleCreateSuccess = async () => {
    await mutate();
  };
  if (isLoading) {
    return <FullPageLoader />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">Failed to load roles</p>
          <button
            onClick={() => mutate()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ReadPermissionGate resource={PERMISSION_RESOURCES.PERMISSION_MANAGEMENT}>
      <div className="p-4">
        <div className="mb-6 flex justify-between items-center">
          <p className="text-gray-600 dark:text-gray-300">
            Manage permissions for different admin roles. Changes will be
            applied immediately.
          </p>
          <CreatePermissionGate
            resource={PERMISSION_RESOURCES.PERMISSION_MANAGEMENT}
          >
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              Create New Role
            </Button>
          </CreatePermissionGate>
        </div>

        <div className="space-y-6">
          {roles.map(role => (
            <RolePermissionsCard
              key={role.id}
              role={role}
              onUpdate={handleUpdatePermissions}
              onDeleteSuccess={handleDeleteSuccess}
              isUpdating={updating === role.id}
            />
          ))}
        </div>

        {roles.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No roles found</p>
          </div>
        )}

        <CreatePermissionGate
          resource={PERMISSION_RESOURCES.PERMISSION_MANAGEMENT}
        >
          <CreateRoleModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={handleCreateSuccess}
          />
        </CreatePermissionGate>
      </div>
    </ReadPermissionGate>
  );
};
